import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Capacitor } from "@capacitor/core";

// Cookie/analytics consent banner.
//
// WEB ONLY. The native app never loads Google Analytics (index.html gates that on
// hostname), so there is nothing to consent to there and a banner would just be a
// modal in the reviewer's face. The gate is the native check rather than the
// hostname because the iOS webview also serves from localhost — testing the
// banner on a dev server would be impossible otherwise.
//
// The ePrivacy Directive wants the analytics cookie asked for before it is set,
// so index.html holds gtag back until this writes 'granted'.

const KEY = "btn-analytics-consent";

function isWebSite() {
  return typeof window !== "undefined" && !Capacitor.isNativePlatform();
}

function readChoice() {
  try {
    return localStorage.getItem(KEY);
  } catch (e) {
    return null;
  }
}

function writeChoice(value) {
  try {
    localStorage.setItem(KEY, value);
  } catch (e) {
    // private mode — the banner simply asks again next visit
  }
}

// Footer link uses this to let someone change their mind.
export function reopenCookieChoice() {
  try {
    localStorage.removeItem(KEY);
  } catch (e) {}
  window.dispatchEvent(new Event("btn-cookie-reopen"));
}

// The footer link is offered on every web render rather than only after a choice
// is stored: reading localStorage during render does not re-run when the banner
// writes to it, so a "has chosen?" test left the link missing until a reload.
export function cookieSettingsAvailable() {
  return isWebSite();
}

function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isWebSite()) return undefined;
    setVisible(readChoice() === null);
    const reopen = () => setVisible(true);
    window.addEventListener("btn-cookie-reopen", reopen);
    return () => window.removeEventListener("btn-cookie-reopen", reopen);
  }, []);

  if (!visible) return null;

  const accept = () => {
    writeChoice("granted");
    setVisible(false);
    if (typeof window.btnLoadAnalytics === "function") window.btnLoadAnalytics();
  };

  const decline = () => {
    writeChoice("denied");
    setVisible(false);
  };

  return (
    <div className="cookie-banner" role="dialog" aria-label="Analytics cookies">
      <div className="cookie-banner-text">
        We'd like to count visits with Google Analytics so we know which analyses people
        actually read. That's the only cookie we'd set — no advertising, no tracking you
        across other sites, nothing sold. Decline and the site works exactly the same.{" "}
        <Link to="/privacy">Privacy Policy</Link>
      </div>
      <div className="cookie-banner-actions">
        <button className="cookie-btn cookie-btn-secondary" onClick={decline}>
          Decline
        </button>
        <button className="cookie-btn cookie-btn-primary" onClick={accept}>
          Accept
        </button>
      </div>
    </div>
  );
}

export default CookieConsent;
